import { BarChart3, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { toolAnalyticsService } from '../services/api';
import './css/ToolAnalytics.css';

const COLORS = {
  discount: '#2563eb',
  montage: '#d97706',
  returning: '#0f766e',
  newUsers: '#5eead4',
  retention: '#7c3aed',
  grid: '#e2e8f0',
  axis: '#64748b',
  cursor: '#f1f5f9',
};
const PARTIAL_OPACITY = 0.4;
const CHART_HEIGHT = 280;
// The same syncId links hover across all three charts; it works by index, so they share one row set.
const SYNC_ID = 'tool-analytics';
const RANGES = [
  { key: '12', label: '12 tyg.', weeks: 12 },
  { key: '26', label: '26 tyg.', weeks: 26 },
  { key: 'all', label: 'Cały rok', weeks: null },
];
const NOT_STARTED = 'jeszcze nie uruchomiono';

// ISO year of today's date in Warsaw — matches the backend's ISO-week logic
// (e.g. 1 Jan 2027 still belongs to ISO week 53 of 2026).
const isoYear = isoDate => {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + 3 - ((date.getUTCDay() + 6) % 7));
  return date.getUTCFullYear();
};
// ISO week number of a YYYY-MM-DD date (the week containing that date's Thursday).
const isoWeek = isoDate => {
  const [year, month, day] = isoDate.split('-').map(Number);
  const thursday = new Date(Date.UTC(year, month - 1, day));
  thursday.setUTCDate(thursday.getUTCDate() + 3 - ((thursday.getUTCDay() + 6) % 7));
  return Math.ceil(((thursday - Date.UTC(thursday.getUTCFullYear(), 0, 1)) / 86400000 + 1) / 7);
};
const currentYear = isoYear(new Intl.DateTimeFormat('en-CA', {
  year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Europe/Warsaw',
}).format(new Date()));
const availableYears = Array.from(
  { length: Math.max(1, currentYear - 2025) }, (_, index) => 2026 + index,
);

const numberFormat = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 1 });
const fmt = value => (value === null || value === undefined ? '—' : numberFormat.format(value));
const fmtPercent = value => (value === null || value === undefined ? '—' : `${fmt(value)}%`);

const formatDate = value => {
  if (!value) return '—';
  const [year, month, day] = value.slice(0, 10).split('-');
  return `${day}.${month}.${year}`;
};

const weekEnd = weekStart => {
  const [year, month, day] = weekStart.split('-').map(Number);
  const end = new Date(Date.UTC(year, month - 1, day + 6));
  return `${String(end.getUTCDate()).padStart(2, '0')}.${String(end.getUTCMonth() + 1).padStart(2, '0')}.${end.getUTCFullYear()}`;
};

const weekLabel = row => `T${String(row.week_number).padStart(2, '0')}`;
const startLabel = start => (start?.first_used_at
  ? `${formatDate(start.first_used_at)} (${weekLabel({ week_number: isoWeek(start.first_used_at.slice(0, 10)) })})`
  : '—');
const weekRange = row => `${weekLabel(row)} · ${formatDate(row.week_start)}–${weekEnd(row.week_start)}`;
const totalOperations = row => (row.discount_operations ?? 0) + (row.montage_operations ?? 0);
const hasStarted = (row, start) => Boolean(start?.week_start) && row.week_start >= start.week_start;


const signed = (diff, suffix) => {
  const sign = diff > 0 ? '+' : diff < 0 ? '−' : '±';
  return { trend: diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat', text: `${sign}${fmt(Math.abs(diff))}${suffix}` };
};

// KPIs describe the last *complete* week: comparing a partial week with a full one would always look like a drop.
const buildKpis = (rows, year) => {
  const completed = rows.filter(row => !row.is_partial);
  if (completed.length === 0) return null;
  const week = completed[completed.length - 1];
  const previous = completed.length > 1 ? completed[completed.length - 2] : null;

  const operationsDelta = () => {
    if (!previous) return null;
    const before = totalOperations(previous);
    if (before === 0) return signed(totalOperations(week), ' t/t');
    return signed(Math.round(((totalOperations(week) - before) / before) * 100), '% t/t');
  };
  const retentionDelta = previous && week.retention_percent !== null && previous.retention_percent !== null
    ? signed(Math.round((week.retention_percent - previous.retention_percent) * 10) / 10, ' pp t/t')
    : null;

  return {
    week,
    previous,
    items: [
      { label: 'Operacje', value: fmt(totalOperations(week)), delta: operationsDelta() },
      {
        label: 'Aktywni użytkownicy',
        value: fmt(week.unique_users),
        delta: previous ? signed(week.unique_users - previous.unique_users, ' t/t') : null,
      },
      { label: 'Retencja t/t', value: fmtPercent(week.retention_percent), delta: retentionDelta },
      {
        label: `Operacje w ${year}`,
        value: fmt(rows.reduce((sum, row) => sum + totalOperations(row), 0)),
        hint: 'łącznie z bieżącym tygodniem',
      },
    ],
  };
};

// Weighted by the previous-week audience, so a week with 3 users doesn't count as much as one with 30.
const weightedRetention = rows => {
  const completed = rows.filter(row => !row.is_partial && row.previous_users > 0);
  const previous = completed.reduce((sum, row) => sum + row.previous_users, 0);
  if (previous === 0) return null;
  const returning = completed.reduce((sum, row) => sum + row.returning_users, 0);
  return Math.round((1000 * returning) / previous) / 10;
};

// Split retention into a solid line (complete weeks) and a faint dashed link into the week in progress.
const withRetentionSegments = rows => rows.map((row, index) => ({
  ...row,
  retention_complete: row.is_partial ? null : row.retention_percent,
  retention_partial: row.is_partial || rows[index + 1]?.is_partial ? row.retention_percent : null,
}));

const gridProps = { strokeDasharray: '3 3', vertical: false, stroke: COLORS.grid };
const xAxisProps = {
  dataKey: 'label',
  tickLine: false,
  axisLine: { stroke: COLORS.grid },
  tick: { fill: COLORS.axis, fontSize: 12 },
  tickMargin: 8,
  interval: 'preserveStartEnd',
  minTickGap: 12,
};
const yAxisProps = { width: 44, tickLine: false, axisLine: false, tick: { fill: COLORS.axis, fontSize: 12 } };
const countAxisProps = { ...yAxisProps, allowDecimals: false, domain: [0, max => Math.max(1, Math.ceil(max * 1.1))] };
const chartMargin = { top: 16, right: 16, bottom: 0, left: 0 };

// Week in progress is drawn lighter so a partial drop isn't read as a trend.
const partialCells = (rows, color) => rows.map(row => (
  <Cell key={row.week_start} fill={color} fillOpacity={row.is_partial ? PARTIAL_OPACITY : 1} />
));

const PartialDot = ({ cx, cy, payload, index }) => (payload?.is_partial && cy !== null && cy !== undefined
  ? <circle key={`partial-${index}`} cx={cx} cy={cy} r={4} fill="#fff" stroke={COLORS.retention} strokeWidth={2} />
  : <g key={`partial-${index}`} />);

const tooltipFor = renderBody => ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return <div className="ta-tooltip">
    <strong>{weekRange(row)}</strong>
    {row.is_partial && <small>Tydzień w toku — dane niepełne.</small>}
    {renderBody(row)}
  </div>;
};

const OperationsTooltip = tooltipFor(row => <>
  <span>Rabatowanie: <b>{row.discount_operations === null ? NOT_STARTED : fmt(row.discount_operations)}</b></span>
  <span>Naliczanie montażu: <b>{row.montage_operations === null ? NOT_STARTED : fmt(row.montage_operations)}</b></span>
  <span>Łącznie: <b>{fmt(totalOperations(row))}</b></span>
</>);

const UsersTooltip = tooltipFor(row => <>
  <span>Unikalni łącznie: <b>{fmt(row.unique_users)}</b></span>
  <span>Powracający: <b>{fmt(row.returning_users)}</b></span>
  <span>Nowi względem poprzedniego tygodnia: <b>{fmt(row.new_vs_previous)}</b></span>
  <span>Rabatowanie: {fmt(row.discount_users)} · Montaż: {fmt(row.montage_users)}</span>
  <small>Osoba używająca obu narzędzi jest liczona raz.</small>
</>);

const RetentionTooltip = tooltipFor(row => <>
  <span>Retencja: <b>{fmtPercent(row.retention_percent)}</b></span>
  <span>Powracający: <b>{fmt(row.returning_users)}</b> z {fmt(row.previous_users)}</span>
  {row.previous_users === 0 && <small>Brak użytkowników w poprzednim tygodniu — retencja jest nieokreślona.</small>}
</>);

const ChartLegend = ({ items }) => (
  <ul className="ta-legend">
    {items.map(item => <li key={item.label}>
      <span className={`ta-swatch ta-swatch-${item.shape || 'box'}`} style={{ '--swatch-color': item.color, opacity: item.opacity }} aria-hidden="true" />
      {item.label}
    </li>)}
  </ul>
);

const ChartPanel = ({ title, description, legend, children }) => (
  <section className="ta-panel" aria-label={title}>
    <header className="ta-panel-header">
      <div>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <ChartLegend items={legend} />
    </header>
    <div className="ta-chart">
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>{children}</ResponsiveContainer>
    </div>
  </section>
);

const KpiRow = ({ kpis }) => (
  <section className="ta-kpis" aria-label="Podsumowanie ostatniego pełnego tygodnia">
    <p className="ta-kpis-caption">
      Ostatni pełny tydzień: <strong>{weekRange(kpis.week)}</strong>
      {kpis.previous && <> · porównanie z {weekLabel(kpis.previous)}</>}
    </p>
    <div className="ta-kpi-grid">
      {kpis.items.map(item => <div className="ta-kpi" key={item.label}>
        <span className="ta-kpi-label">{item.label}</span>
        <strong className="ta-kpi-value">{item.value}</strong>
        <span className={`ta-kpi-delta${item.delta ? ` ta-kpi-delta-${item.delta.trend}` : ''}`}>
          {item.delta?.text || item.hint || 'brak poprzedniego tygodnia'}
        </span>
      </div>)}
    </div>
  </section>
);

const ToolAnalytics = () => {
  const [year, setYear] = useState(currentYear);
  const [range, setRange] = useState('26');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    toolAnalyticsService.getWeeklyUsage(year).then(result => {
      if (active) setData(result);
    }).catch(() => {
      if (active) setError('Nie udało się pobrać analizy. Spróbuj ponownie.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [year, refreshKey]);

  // A refresh keeps the current charts on screen; a year change clears them so old data never sits under a new year.
  const changeYear = event => {
    setData(null);
    setYear(Number(event.target.value));
  };

  const rows = useMemo(() => (data?.weeks || []).map(row => ({ ...row, label: weekLabel(row) })), [data]);
  const starts = useMemo(
    () => Object.fromEntries((data?.starts || []).map(start => [start.operation, start])),
    [data],
  );
  const discountStart = starts.analyze;
  const montageStart = starts.montage_analyze;
  const kpis = useMemo(() => buildKpis(rows, data?.year ?? year), [rows, data, year]);

  const visibleRows = useMemo(() => {
    const { weeks } = RANGES.find(item => item.key === range);
    return weeks ? rows.slice(-weeks) : rows;
  }, [rows, range]);
  const operationRows = useMemo(() => visibleRows.map(row => ({
    ...row,
    discount_operations: hasStarted(row, discountStart) ? row.discount_operations : null,
    montage_operations: hasStarted(row, montageStart) ? row.montage_operations : null,
  })), [visibleRows, discountStart, montageStart]);
  const retentionRows = useMemo(() => withRetentionSegments(visibleRows), [visibleRows]);
  const averageRetention = useMemo(() => weightedRetention(visibleRows), [visibleRows]);

  const partialLegend = visibleRows.some(row => row.is_partial)
    ? [{ label: 'Tydzień w toku', color: COLORS.axis, opacity: PARTIAL_OPACITY }]
    : [];

  return <div className="page-container ta-page">
    <div className="page-header ta-header">
      <div>
        <h2 className="module-page-title"><BarChart3 aria-hidden="true" /> Analiza narzędzi (dla handlowców)</h2>
        <p>Rabatowanie ofert aluminium i naliczanie montażu</p>
      </div>
      <div className="ta-actions">
        <div className="ta-range" role="group" aria-label="Zakres tygodni">
          {RANGES.map(item => <button
            key={item.key} type="button" aria-pressed={range === item.key}
            className={range === item.key ? 'is-active' : undefined}
            onClick={() => setRange(item.key)}
          >{item.label}</button>)}
        </div>
        <label htmlFor="ta-year">Rok</label>
        <select id="ta-year" value={year} onChange={changeYear}>
          {availableYears.map(value => <option key={value} value={value}>{value}</option>)}
        </select>
        <button type="button" onClick={() => setRefreshKey(key => key + 1)} disabled={loading}>
          <RefreshCw size={16} aria-hidden="true" className={loading ? 'ta-spin' : undefined} /> Odśwież
        </button>
      </div>
    </div>

    <div className="page-content ta-content">
      {error && <div className="ta-error" role="alert">{error}</div>}
      {!data && loading && <p role="status">Ładowanie analizy…</p>}
      {data && <>
        <div className="ta-starts" aria-label="Pierwsze użycie narzędzi">
          <div><span className="ta-dot ta-dot-discount" /><strong>Start rabatowania</strong><span>{startLabel(discountStart)}</span></div>
          <div><span className="ta-dot ta-dot-montage" /><strong>Start naliczania montażu</strong><span>{startLabel(montageStart)}</span></div>
        </div>

        {rows.length === 0 ? <p className="ta-empty">Brak danych dla wybranego roku.</p> : <>
          {kpis && <KpiRow kpis={kpis} />}

          <ChartPanel
            title="Tygodniowa liczba operacji"
            legend={[
              { label: 'Rabatowanie', color: COLORS.discount },
              { label: 'Naliczanie montażu', color: COLORS.montage },
              ...partialLegend,
            ]}
          >
            <BarChart data={operationRows} syncId={SYNC_ID} margin={chartMargin} barCategoryGap="20%">
              <CartesianGrid {...gridProps} />
              <XAxis {...xAxisProps} />
              <YAxis {...countAxisProps} />
              <Tooltip content={<OperationsTooltip />} cursor={{ fill: COLORS.cursor }} filterNull={false} />
              <Bar dataKey="discount_operations" name="Rabatowanie" stackId="operations" fill={COLORS.discount} maxBarSize={28}>
                {partialCells(operationRows, COLORS.discount)}
              </Bar>
              <Bar dataKey="montage_operations" name="Naliczanie montażu" stackId="operations" fill={COLORS.montage} maxBarSize={28}>
                {partialCells(operationRows, COLORS.montage)}
              </Bar>
            </BarChart>
          </ChartPanel>

          <ChartPanel
            title="Unikalni użytkownicy w tygodniu"
            legend={[
              { label: 'Powracający', color: COLORS.returning },
              { label: 'Nowi względem poprzedniego tygodnia', color: COLORS.newUsers },
              ...partialLegend,
            ]}
          >
            <BarChart data={visibleRows} syncId={SYNC_ID} margin={chartMargin} barCategoryGap="20%">
              <CartesianGrid {...gridProps} />
              <XAxis {...xAxisProps} />
              <YAxis {...countAxisProps} />
              <Tooltip content={<UsersTooltip />} cursor={{ fill: COLORS.cursor }} filterNull={false} />
              <Bar dataKey="returning_users" name="Powracający" stackId="users" fill={COLORS.returning} maxBarSize={28}>
                {partialCells(visibleRows, COLORS.returning)}
              </Bar>
              <Bar dataKey="new_vs_previous" name="Nowi" stackId="users" fill={COLORS.newUsers} maxBarSize={28}>
                {partialCells(visibleRows, COLORS.newUsers)}
              </Bar>
            </BarChart>
          </ChartPanel>

          <ChartPanel
            title="Retencja tydzień do tygodnia"
            legend={[
              { label: 'Retencja', color: COLORS.retention, shape: 'line' },
              ...(averageRetention === null ? [] : [{ label: `Średnia ważona ${fmtPercent(averageRetention)}`, color: COLORS.axis, shape: 'dashed' }]),
              ...partialLegend,
            ]}
          >
            <LineChart data={retentionRows} syncId={SYNC_ID} margin={chartMargin}>
              <CartesianGrid {...gridProps} />
              <XAxis {...xAxisProps} />
              <YAxis {...yAxisProps} domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} unit="%" />
              <Tooltip content={<RetentionTooltip />} cursor={{ stroke: COLORS.grid, strokeWidth: 1 }} filterNull={false} />
              {averageRetention !== null && <ReferenceLine y={averageRetention} stroke={COLORS.axis} strokeDasharray="4 4" />}
              <Line
                dataKey="retention_complete" name="Retencja" stroke={COLORS.retention} strokeWidth={2}
                dot={{ r: 3, fill: COLORS.retention, strokeWidth: 0 }} activeDot={{ r: 5 }} connectNulls={false}
              />
              <Line
                dataKey="retention_partial" name="Retencja (w toku)" stroke={COLORS.retention} strokeWidth={2}
                strokeOpacity={PARTIAL_OPACITY} strokeDasharray="4 4" dot={PartialDot} activeDot={false}
                connectNulls legendType="none"
              />
            </LineChart>
          </ChartPanel>
        </>}
        <p className="ta-footnote">„Nowi względem poprzedniego tygodnia” obejmuje także osoby wracające po dłuższej przerwie. Jeśli poprzedni tydzień miał 0 użytkowników, retencja pozostaje "nieokreślona".</p>
      </>}
    </div>
  </div>;
};

export default ToolAnalytics;
